/**
 * Browser side of a portal voice call with an OpenAI Realtime agent (WebRTC).
 *
 * The SDP offer goes to the KC3 API, which completes the exchange with OpenAI
 * using the tenant key — the browser only ever sees the answer SDP. Audio is
 * a normal WebRTC media path (mic track out, remote track into an <audio>
 * element); events arrive on the "oai-events" data channel:
 *
 *   ← input_audio_buffer.speech_started · output_audio_buffer.started/stopped
 *   ← conversation.item.input_audio_transcription.completed (caller line)
 *   ← response.output_audio_transcript.done (AI line)
 *   ← response.function_call_arguments.done (KC3 tool call)
 *   → conversation.item.create {function_call_output} + response.create
 *
 * Interruptions and replies are decided by BargeInGate (the session runs with
 * interrupt_response/create_response off): background noise and "haan / hmm"
 * no longer cut the agent off and make it repeat itself.
 */
import { BargeInGate, classifyCallerLine } from '@kc3/types'

import type { BrowserCallHandlers } from './eleven-browser-call.js'
import { startCallRecorder, type CallRecorder } from './call-recorder.js'

export interface OpenAiCallHandlers extends BrowserCallHandlers {
  /** Persist a transcript line (OpenAI does not keep one for us). */
  onTurn: (role: 'caller' | 'bot', text: string, itemId: string) => void
}

export class OpenAiBrowserCall {
  private pc: RTCPeerConnection | null = null
  private dc: RTCDataChannel | null = null
  private stream: MediaStream | null = null
  private audioEl: HTMLAudioElement | null = null
  private ctx: AudioContext | null = null
  private levelTimer: ReturnType<typeof setInterval> | null = null
  private speaking = false
  private closed = false
  private recorder: CallRecorder | null = null
  private readonly gate = new BargeInGate({
    respond: () => this.send({ type: 'response.create' }),
    interrupt: () => {
      this.send({ type: 'response.cancel' })
      this.send({ type: 'output_audio_buffer.clear' })
    },
    drop: (itemId) => { if (itemId) this.send({ type: 'conversation.item.delete', item_id: itemId }) },
    duck: (on) => { if (this.audioEl) this.audioEl.volume = on ? 0.35 : 1 },
    isPlaying: () => this.speaking,
  })
  remoteStream: MediaStream | null = null

  /** `record: false` skips the local call recording (agent-portal assistant). */
  constructor(private readonly h: OpenAiCallHandlers, private readonly opts: { record?: boolean } = {}) {}

  /** Mic + peer connection; returns the offer SDP to send to the API. */
  async prepare(): Promise<string> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
    })
    const pc = new RTCPeerConnection()
    this.pc = pc
    for (const t of this.stream.getAudioTracks()) pc.addTrack(t, this.stream)
    this.audioEl = new Audio()
    this.audioEl.autoplay = true
    pc.ontrack = (ev) => {
      this.remoteStream = ev.streams[0] ?? new MediaStream([ev.track])
      if (this.audioEl) this.audioEl.srcObject = this.remoteStream
    }
    pc.onconnectionstatechange = () => {
      if (['failed', 'closed', 'disconnected'].includes(pc.connectionState) && !this.closed) {
        this.teardown()
        this.h.onClosed(pc.connectionState)
      }
    }
    this.dc = pc.createDataChannel('oai-events')
    this.dc.onmessage = (ev) => { void this.dispatch(ev.data) }
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    // Non-trickle exchange: give ICE gathering up to 2 s to finish.
    await new Promise<void>((resolve) => {
      if (pc.iceGatheringState === 'complete') { resolve(); return }
      const t = setTimeout(resolve, 2000)
      pc.addEventListener('icegatheringstatechange', () => { if (pc.iceGatheringState === 'complete') { clearTimeout(t); resolve() } })
    })
    this.startLevelMeter()
    return pc.localDescription?.sdp ?? offer.sdp ?? ''
  }

  /** Apply the answer from the API; resolves when the event channel opens. */
  async start(answerSdp: string, greetingInstruction: string, language?: string | null): Promise<void> {
    if (!this.pc || !this.dc) throw new Error('Call not prepared')
    this.gate.setLanguage(language)
    await this.pc.setRemoteDescription({ type: 'answer', sdp: answerSdp })
    const dc = this.dc
    await new Promise<void>((resolve, reject) => {
      if (dc.readyState === 'open') { resolve(); return }
      const t = setTimeout(() => reject(new Error('The assistant did not answer.')), 12_000)
      dc.onopen = () => { clearTimeout(t); resolve() }
    })
    this.h.onStarted(null)
    if (this.opts.record !== false && this.ctx && this.stream) this.recorder = startCallRecorder(this.ctx, [this.stream, ...(this.remoteStream ? [this.remoteStream] : [])])
    this.gate.responseCreated()
    this.send({ type: 'response.create', response: { instructions: greetingInstruction } })
  }

  setMuted(muted: boolean): void {
    this.stream?.getAudioTracks().forEach((t) => { t.enabled = !muted })
  }

  /** WebRTC plays in real time; "still talking" is the output buffer state. */
  pendingMs(): number {
    return this.speaking ? 500 : 0
  }

  /** After end_call: true = goodbye spoken, hang up; false = the caller spoke, keep talking. */
  waitForGoodbye(maxMs = 12_000): Promise<boolean> {
    return this.gate.waitForGoodbye(maxMs)
  }

  /** Stop recording and return the mixed call audio (call before stop()). */
  async finishRecording(): Promise<Blob | null> {
    const r = this.recorder
    this.recorder = null
    return r ? r.stop() : null
  }

  stop(): void {
    if (this.closed) return
    this.teardown()
  }

  private teardown(): void {
    this.closed = true
    this.gate.dispose()
    if (this.levelTimer) clearInterval(this.levelTimer)
    try { this.dc?.close() } catch { /* already closed */ }
    try { this.pc?.close() } catch { /* already closed */ }
    this.stream?.getTracks().forEach((t) => t.stop())
    if (this.audioEl) this.audioEl.srcObject = null
    void this.ctx?.close().catch(() => undefined)
    this.h.onSpeaking(false)
  }

  private startLevelMeter(): void {
    if (!this.stream) return
    this.ctx = new AudioContext()
    const an = this.ctx.createAnalyser()
    an.fftSize = 512
    this.ctx.createMediaStreamSource(this.stream).connect(an)
    const buf = new Float32Array(an.fftSize)
    this.levelTimer = setInterval(() => {
      an.getFloatTimeDomainData(buf)
      let peak = 0
      for (const v of buf) peak = Math.max(peak, Math.abs(v))
      this.h.onLevel(peak)
    }, 120)
  }

  private send(o: unknown): void {
    if (this.dc?.readyState === 'open') this.dc.send(JSON.stringify(o))
  }

  private async dispatch(raw: unknown): Promise<void> {
    let m: Record<string, unknown>
    try { m = JSON.parse(String(raw)) as Record<string, unknown> } catch { return }
    const type = String(m['type'] ?? '')
    switch (type) {
      case 'output_audio_buffer.started':
        this.speaking = true
        this.h.onSpeaking(true)
        break
      case 'output_audio_buffer.stopped':
      case 'output_audio_buffer.cleared':
        this.speaking = false
        this.h.onSpeaking(false)
        break
      case 'response.created':
        this.gate.responseCreated()
        break
      case 'response.done':
        this.gate.responseDone()
        break
      case 'response.output_audio_transcript.delta':
      case 'response.audio_transcript.delta':
        this.gate.botDelta(String(m['delta'] ?? ''))
        break
      case 'input_audio_buffer.speech_started':
        this.gate.speechStarted()
        break
      case 'input_audio_buffer.committed':
        this.gate.committed(String(m['item_id'] ?? ''))
        break
      case 'conversation.item.input_audio_transcription.failed':
        this.gate.transcript(String(m['item_id'] ?? ''), null)
        break
      case 'conversation.item.input_audio_transcription.completed': {
        const t = String(m['transcript'] ?? '').trim()
        const itemId = String(m['item_id'] ?? '')
        const heldNoise = this.gate.botSpeaking && classifyCallerLine(t) === 'noise'
        this.gate.transcript(itemId, t)
        if (t && !heldNoise) { this.h.onCaption('caller', t); this.h.onTurn('caller', t, itemId) }
        break
      }
      case 'response.output_audio_transcript.done':
      case 'response.audio_transcript.done': {
        const t = String(m['transcript'] ?? '').trim()
        if (t) { this.h.onCaption('bot', t); this.h.onTurn('bot', t, String(m['item_id'] ?? '')) }
        break
      }
      case 'response.function_call_arguments.done': {
        let params: Record<string, unknown> = {}
        try { params = JSON.parse(String(m['arguments'] ?? '{}')) as Record<string, unknown> } catch { /* keep {} */ }
        let result: unknown
        try { result = await this.h.onToolCall(String(m['name'] ?? ''), params) } catch { result = { ok: false, error: 'TOOL_FAILED' } }
        this.send({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: m['call_id'], output: JSON.stringify(result) } })
        this.gate.requestResponse()
        break
      }
      default:
        break
    }
  }
}
